/* saif-course-track.js — mid-funnel instrumentation for course pages.
 *
 * Why this exists: the quiz_destination A/B test sends one arm to the free lesson and
 * the other to a course page. The lesson emits seven events; the course page emitted
 * exactly one (`product_viewed`). Comparing those two arms would have made the lesson
 * look better purely because it was measured, and the only shared signal was an order —
 * which at ~1% conversion takes hundreds of visitors per arm to read.
 *
 * So: give the course page a mid-funnel signal comparable to "played the lesson".
 *
 * Keyed off selectors that are stable across BOTH themes rather than the current
 * markup — `form[action*="/cart/add"]` and `<details>` — because live and pre-prod
 * differ substantially on this page and this has to work on both.
 *
 * The A/B arm rides along automatically: saif-ab.js registers it as a PostHog super
 * property, so every event below is splittable by arm with no extra work here.
 */
(function () {
  /* Product pages only — identified by the presence of an add-to-cart form, not by a
     URL pattern, so it keeps working if the routing changes. */
  var start = Date.now();
  var forms = document.querySelectorAll('form[action*="/cart/add"]');
  if (!forms.length) return;

  var track = function (name, props) {
    if (window.SaifTrack) window.SaifTrack(name, props);
    else if (window.posthog && posthog.capture) posthog.capture(name, props);
  };

  var handle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || '';
  var base = function () {
    return { product: handle, page_height: document.documentElement.scrollHeight };
  };

  /* ---------- 1. add to cart — the signal that was missing entirely ---------- */
  [].forEach.call(forms, function (form, i) {
    form.addEventListener('submit', function () {
      var d = base();
      /* Which of the several buy blocks on the page did the work? A page this tall
         (6,676px on live) repeats the CTA, and knowing whether people buy from the top
         block or only after reading is worth more than the raw count. */
      d.form_index = i;
      d.position = Math.round((form.getBoundingClientRect().top + window.scrollY)
        / Math.max(1, document.documentElement.scrollHeight) * 100);
      try {
        var fd = new FormData(form);
        d.variant_id = String(fd.get('id') || '');
        /* Present only when they picked instalments — geo-gated, so its absence is
           itself informative in markets where plans are not offered. */
        var plan = fd.get('selling_plan');
        d.selling_plan = plan ? String(plan) : null;
        d.on_payment_plan = !!plan;
        d.quantity = String(fd.get('quantity') || '1');
      } catch (e) { /* FormData unsupported — the event still fires */ }
      track('course_add_to_cart', d);
    });
  });

  /* Buy-now / checkout shortcuts bypass the form submit on some themes. */
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest && e.target.closest(
      '[data-buy-now], .shopify-payment-button__button, a[href*="/checkout"]');
    if (!el) return;
    var d = base();
    d.control = el.getAttribute('data-buy-now') !== null ? 'buy_now'
      : (el.className || '').toString().indexOf('shopify-payment-button') !== -1 ? 'express_checkout'
      : 'checkout_link';
    track('course_checkout_shortcut', d);
  }, true);

  /* ---------- 2. what they open — tells us which doubts they have ---------- */
  [].forEach.call(document.querySelectorAll('details'), function (d, di) {
    d.addEventListener('toggle', function () {
      if (!d.open) return;                       // closing is not a signal
      var sum = d.querySelector('summary');
      var label = sum ? (sum.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80) : '';
      /* Two of the six accordions on the course page have an icon-only summary that
         reads as empty text. Bailing on a blank label dropped those sections silently,
         so fall back to a heading inside, then to an id/class, then to the index —
         the point is to know WHICH section was opened, not to have a pretty name. */
      if (!label) {
        var h = d.querySelector('h2, h3, h4, [class*=title], [class*=heading]');
        label = h ? (h.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80) : '';
      }
      if (!label) label = d.id || String(d.className || '').split(' ')[0] || ('section_' + di);
      var p = base();
      p.section = label;
      p.section_index = di;
      track('course_section_opened', p);
    });
  });

  /* ---------- 3. how far they read ----------
     $pageleave already carries max scroll, but only as one number at exit. Milestones
     fire as they happen, which is what makes them comparable to the lesson's 25/50/75
     video marks — the whole point of this file. */
  var marks = {};
  var onScroll = function () {
    var h = document.documentElement.scrollHeight - window.innerHeight;
    if (h <= 0) return;
    var pct = (window.scrollY / h) * 100;
    [25, 50, 75, 100].forEach(function (m) {
      if (pct >= m - 1 && !marks[m]) {
        marks[m] = true;
        var p = base();
        p.milestone = m;
        track('course_scroll', p);
      }
    });
    if (marks[100]) window.removeEventListener('scroll', onScroll);
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  /* ---------- 4. did they ever see the price? ----------
     A visitor who never reaches the buy block has a different problem from one who
     reaches it and does not click, and today those two look identical. */
  if ('IntersectionObserver' in window) {
    var seen = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting || seen) return;
        seen = true;
        io.disconnect();
        var p = base();
        p.seconds_to_reach = Math.round((Date.now() - start) / 1000);
        track('course_price_seen', p);
      });
    }, { threshold: 0.5 });
    io.observe(forms[0]);
  }

  /* ---------- 5. anything audible on the page ---------- */
  [].forEach.call(document.querySelectorAll('audio, video'), function (m) {
    m.addEventListener('play', function () {
      var p = base();
      p.src = (m.currentSrc || m.src || '').split('/').pop().split('?')[0].slice(0, 60);
      track('course_media_played', p);
    }, { once: true });
  });

})();
